import React, { useState, useMemo, useEffect } from 'react';
import { CryptoSelect } from '../atoms/CryptoSelect';
import type { CryptoCurrency } from '../../utils/coingecko';
import type { Payout } from '../../types/payout';
import {
  detectCryptoIdConflicts,
  normalizeConflict,
  type CryptoIdConflict,
} from '../../utils/cryptoConflicts';

interface CryptoConflictBannerProps {
  payouts: Payout[];
  onSave: (updatedPayouts: Payout[]) => Promise<void>;
}

/**
 * Detects when the same crypto symbol appears under multiple different IDs
 * across transactions (e.g., payouts imported as 'pearl' vs a swap entered
 * manually as 'custom-prl'). Lets the user pick the canonical ID — either
 * one of the existing variants, or a different CoinGecko coin — then
 * normalises all affected transactions in one click.
 */
export const CryptoConflictBanner: React.FC<CryptoConflictBannerProps> = ({
  payouts,
  onSave,
}) => {
  const conflicts = useMemo(() => detectCryptoIdConflicts(payouts), [payouts]);

  // For each conflict (keyed by symbol): the chosen canonical CryptoCurrency
  const [canonicals, setCanonicals] = useState<Record<string, CryptoCurrency | null>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialise slots — no auto-selection so the user explicitly chooses
  useEffect(() => {
    if (conflicts.length === 0) return;
    setCanonicals((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const c of conflicts) {
        if (!(c.symbol in next)) {
          next[c.symbol] = null;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [conflicts]);

  if (conflicts.length === 0) return null;

  const readyCount = Object.values(canonicals).filter(Boolean).length;

  const handleApply = async () => {
    const entries = Object.entries(canonicals).filter(([, v]) => v !== null);
    if (entries.length === 0) return;

    setIsSaving(true);
    try {
      let updated = [...payouts];
      for (const [symbol, canonical] of entries) {
        const conflict = conflicts.find((c) => c.symbol === symbol);
        if (!conflict || !canonical) continue;
        // Replace every other variant with the chosen canonical
        for (const variant of conflict.variants) {
          if (variant.id === canonical.id) continue;
          updated = normalizeConflict(updated, variant.id, canonical);
        }
      }
      await onSave(updated);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto mb-6">
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <svg
            className="w-5 h-5 text-orange-500 mt-0.5 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <div>
            <p className="text-sm font-semibold text-orange-800">
              {conflicts.length} symbole{conflicts.length > 1 ? 's' : ''} avec
              des identifiants incohérents
            </p>
            <p className="text-xs text-orange-700 mt-0.5">
              Ces cryptos apparaissent sous plusieurs IDs dans vos transactions.
              Choisissez l'ID à conserver — l'inventaire et la déclaration
              fiscale seront corrigés automatiquement.
            </p>
          </div>
        </div>

        {/* One row per conflict */}
        <div className="space-y-3">
          {conflicts.map((conflict) => (
            <ConflictRow
              key={conflict.symbol}
              conflict={conflict}
              canonical={canonicals[conflict.symbol] ?? null}
              onSelect={(crypto) =>
                setCanonicals((prev) => ({ ...prev, [conflict.symbol]: crypto }))
              }
            />
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleApply}
            disabled={readyCount === 0 || isSaving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving
              ? 'Normalisation…'
              : `Normaliser${readyCount > 0 ? ` (${readyCount})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row for one conflicting symbol
// ---------------------------------------------------------------------------

interface ConflictRowProps {
  conflict: CryptoIdConflict;
  canonical: CryptoCurrency | null;
  onSelect: (crypto: CryptoCurrency | null) => void;
}

const ConflictRow: React.FC<ConflictRowProps> = ({
  conflict,
  canonical,
  onSelect,
}) => {
  // Track whether the user is using the CoinGecko search or picked a variant
  const [useSearch, setUseSearch] = useState(false);

  const handlePickVariant = (variant: { id: string; name: string }) => {
    setUseSearch(false);
    onSelect({ id: variant.id, symbol: conflict.symbol, name: variant.name });
  };

  const handleUseSearch = (crypto: CryptoCurrency | null) => {
    onSelect(crypto);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-orange-100 px-3 py-3 space-y-2">
      {/* Symbol + tx count */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-orange-900">
          {conflict.symbol}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          ({conflict.payoutCount} transaction
          {conflict.payoutCount > 1 ? 's' : ''})
        </span>
      </div>

      {/* Variant buttons — click one to keep it */}
      <div className="space-y-1">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
          Choisir l'ID à conserver :
        </p>
        <div className="flex flex-wrap gap-2">
          {conflict.variants.map((v) => {
            const isSelected = !useSearch && canonical?.id === v.id;
            const isCustom = v.id.startsWith('custom-');
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => handlePickVariant(v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-all ${
                  isSelected
                    ? 'border-orange-500 bg-orange-100 text-orange-900 font-medium ring-1 ring-orange-400'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                {/* Badge: custom vs real */}
                <span
                  className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                    isCustom
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {isCustom ? 'custom' : 'CoinGecko'}
                </span>
                <code className="font-mono">{v.id}</code>
                <span className="text-gray-400 dark:text-gray-500">({v.name})</span>
                {isSelected && (
                  <svg
                    className="w-3.5 h-3.5 text-orange-500 ml-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            );
          })}

          {/* Option: search a different CoinGecko coin */}
          <button
            type="button"
            onClick={() => {
              setUseSearch(true);
              onSelect(null);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-all ${
              useSearch
                ? 'border-blue-500 bg-blue-50 text-blue-900 font-medium'
                : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-blue-300 hover:text-blue-600'
            }`}
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            Autre coin CoinGecko…
          </button>
        </div>
      </div>

      {/* CoinGecko search — only when "Autre" is selected */}
      {useSearch && (
        <div className="pl-1">
          <CryptoSelect
            value={useSearch && canonical ? canonical : null}
            onChange={handleUseSearch}
            placeholder={`Rechercher le vrai coin pour ${conflict.symbol}…`}
          />
        </div>
      )}

      {/* Summary */}
      {canonical && (
        <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">
          ✓ Toutes les transactions{' '}
          {conflict.variants
            .filter((v) => v.id !== canonical.id)
            .map((v) => (
              <code key={v.id} className="bg-green-100 px-1 rounded mx-0.5">
                {v.id}
              </code>
            ))}{' '}
          seront remplacées par{' '}
          <code className="bg-green-100 px-1 rounded">{canonical.id}</code>.
        </p>
      )}
    </div>
  );
};
